import React from "react";
import { Apple } from "lucide-react";
import { ButtonLink } from "../../../../components/ui/button";
import { FadeUp } from "./FadeUp";
import type { AppStoreLinks, RouteMap } from "../../types";

type AppDownloadSectionProps = {
  routes: RouteMap;
  appStore?: AppStoreLinks | null;
  pitch?: string;
};

const PlayStoreMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M3.6 2.8 14.2 12 3.6 21.2c-.4-.3-.6-.8-.6-1.3V4.1c0-.5.2-1 .6-1.3Z"
    />
    <path
      fill="currentColor"
      d="M14.2 12 17.7 8.9 5.4 1.6c.3-.1.5-.1.8 0L14.2 12Z"
      opacity="0.85"
    />
    <path
      fill="currentColor"
      d="M14.2 12 6.2 22.4c-.3.1-.5.1-.8 0L17.7 15.1 14.2 12Z"
      opacity="0.7"
    />
    <path
      fill="currentColor"
      d="m17.7 8.9 3.1 1.8c.9.5.9 1.9 0 2.4l-3.1 1.8L14.2 12l3.5-3.1Z"
    />
  </svg>
);

type StoreBadgeProps = {
  href?: string | null;
  label: string;
  caption: string;
  comingSoonLabel: string;
  icon: React.ReactNode;
};

const StoreBadge: React.FC<StoreBadgeProps> = ({
  href,
  label,
  caption,
  comingSoonLabel,
  icon,
}) => {
  const live = Boolean(href);
  const className =
    "flex min-w-[12.5rem] items-center gap-3 rounded-xl border border-white/20 bg-black px-4 py-3 text-left text-white shadow-lg";

  const body = (
    <>
      {icon}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[0.65rem] font-medium uppercase tracking-wider text-white/70">
          {caption}
        </span>
        <span className="font-display text-base font-semibold">{label}</span>
      </span>
    </>
  );

  if (live) {
    return (
      <a
        href={href ?? undefined}
        className={`${className} transition-opacity hover:opacity-90`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {body}
      </a>
    );
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <span
        className={`${className} cursor-not-allowed opacity-80`}
        aria-disabled="true"
        aria-label={comingSoonLabel}
      >
        {body}
      </span>
      <span className="text-xs font-semibold uppercase tracking-wider text-premium-300">
        Coming soon
      </span>
    </div>
  );
};

export const AppDownloadSection: React.FC<AppDownloadSectionProps> = ({
  routes,
  appStore,
  pitch = "Quotes, job photos, and licenses. Capture on site. Check a card before it expires.",
}) => (
  <section id="app" className="relative overflow-hidden bg-neutral-900 py-24">
    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <FadeUp>
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-premium-300">
            Toolkit app
          </p>
          <h2 className="mb-6 font-display text-3xl font-bold tracking-tight text-white md:text-5xl">
            Built for the phone in your truck
          </h2>
          <p className="mb-10 text-lg font-light leading-relaxed text-neutral-300 md:text-xl">
            {pitch}
          </p>
          <div className="mb-10 flex flex-col items-center justify-center gap-6 sm:flex-row sm:items-end">
            <StoreBadge
              href={appStore?.iosUrl}
              caption="Download on the"
              label="App Store"
              comingSoonLabel="Download on the App Store, coming soon"
              icon={<Apple className="h-8 w-8 shrink-0" aria-hidden />}
            />
            <StoreBadge
              href={appStore?.androidUrl}
              caption="Get it on"
              label="Google Play"
              comingSoonLabel="Get it on Google Play, coming soon"
              icon={<PlayStoreMark className="h-8 w-8 shrink-0" />}
            />
          </div>
          <ButtonLink
            to={routes.signup}
            size="lg"
            variant="premium"
            className="px-8 py-4 text-lg"
          >
            Start 30-day trial
          </ButtonLink>
        </div>
      </FadeUp>
    </div>
  </section>
);
